import React from 'react';
import { User as UserIcon, LogOut, HeartPulse, Lock, Building2 } from 'lucide-react';
import { User, Role, Company } from '@/types';

interface LayoutProps {
  children: React.ReactNode;
  currentUser: User | null;
  currentCompany?: Company | null;
  onLogout: () => void;
  onLoginClick: () => void;
  onCompanyLoginClick?: () => void;
  appName?: string;
  logoUrl?: string; // Added logoUrl
  onLogoClick?: () => void;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  currentUser,
  currentCompany,
  onLogout,
  onLoginClick,
  onCompanyLoginClick,
  appName = 'K2 OccuHealth Pro',
  logoUrl,
  onLogoClick
}) => {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            type="button"
            onClick={onLogoClick}
            className={`flex items-center gap-3 transition-colors ${onLogoClick ? 'hover:opacity-90 cursor-pointer' : 'cursor-default'}`}
            title={onLogoClick ? 'Go to Home' : undefined}
          >
            <div className="h-10 w-auto flex items-center justify-center overflow-hidden">
              <img
                src={logoUrl || 'https://placehold.co/100x100/009FE3/ffffff?text=K2'}
                alt="Company Logo"
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/100x100/009FE3/ffffff?text=K2';
                }}
              />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">{appName}</h1>
          </button>

          <div className="flex items-center gap-4">
            {currentUser || currentCompany ? (
              <div className="flex items-center gap-4">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-slate-900">
                    {currentUser ? currentUser.name : currentCompany?.name}
                  </p>
                  <p className="text-xs text-slate-500 capitalize">
                    {currentUser ? currentUser.role : 'Company Portal'}
                  </p>
                </div>
                <button
                  onClick={onLogout}
                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={onLoginClick}
                  className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#009FE3] transition-colors px-3 py-2 rounded-lg hover:bg-blue-50"
                >
                  <Lock className="h-4 w-4" />
                  <span>Staff Portal</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-grow">{children}</main>

      <footer className="bg-slate-900 text-slate-400 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start">
            <div className="max-w-md">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src={logoUrl || 'https://placehold.co/50x50/334155/ffffff?text=K2'}
                  className="h-6 w-auto opacity-50 grayscale"
                  alt="Footer Logo"
                />
                <h3 className="text-white font-semibold">{appName}</h3>
              </div>
              <p className="text-sm">
                Leading provider of occupational health services across the UK. Ensuring your
                workforce is safe, healthy, and compliant.
              </p>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-xs">
            &copy; {new Date().getFullYear()} {appName}. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};
